import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { assemblyService, propertyService } from 'services/api';
import { useAuth } from 'context/AuthContext';
import { useMeeting } from 'context/MeetingContext';
import { votacionesAbiertas } from 'components/ConfirmarFinalizarModal';
import useCierreAlerta from 'utils/useCierreAlerta';

// Estado al que pasa el botón de cada fila (Iniciar / Finalizar / Reiniciar). Se manda EXPLÍCITO: el servidor ya no cicla al siguiente estado por
// defecto (#435) y, si otro administrador cambió la asamblea mientras esta pantalla seguía abierta, un clic no avanza el ciclo a ciegas.
const SIGUIENTE_ESTADO = { pendiente: 'en_progreso', en_progreso: 'finalizada', finalizada: 'pendiente' };
export const PAGE_SIZE = 20;

// Lógica de la pantalla (estado, carga y acciones); la página solo pinta.
export default function useAssemblies() {
  const { isSuperAdmin, user, isReadOnly: globalIsReadOnly, getSubscriptionStateForProperty } = useAuth();
  const isAdminMultiProp = user?.role === 'admin';

  // Propiedades del admin (para selector multi-prop)
  const [properties,      setProperties]      = useState([]);
  const [filterProp,      setFilterProp]      = useState('');
  const [selectedPropObj, setSelectedPropObj] = useState(null);

  // Estado de suscripción de la propiedad seleccionada
  const effectiveSub  = selectedPropObj ? getSubscriptionStateForProperty(selectedPropObj) : 'none';
  const isReadOnly    = effectiveSub === 'grace'   ? true  : (!selectedPropObj ? globalIsReadOnly : false);
  const isBlocked     = effectiveSub === 'blocked';

  const [assemblies, setAssemblies] = useState([]);
  const { abrirReunion } = useMeeting();
  // Staff: inicia la reunión y, al cerrar la ventana, la termina para todos.
  const abrirReunionStaff = async (a) => {
    try { await assemblyService.startMeeting(a.id); } catch (_) {}
    abrirReunion({
      assemblyId:   a.id,
      assemblyName: `${a.property?.name || 'Asamblea'} #${a.number_assembly}`,
      isModerator:  true,
      alCerrar:     () => assemblyService.endMeeting(a.id),
    });
  };
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [page,       setPage]       = useState(1);
  const [total,      setTotal]      = useState(0);
  const [alert,      setAlert]      = useState(null);
  const cerrarAlertaEn = useCierreAlerta(setAlert);
  const [avisoTodas, setAvisoTodas] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting,   setDeleting]   = useState(false);
  const [delError,   setDelError]   = useState('');

  // Cargar propiedades del admin para el selector
  useEffect(() => {
    if (isAdminMultiProp || isSuperAdmin) {
      propertyService.selector({ limit: 200 }).then(r => {
        const props = r.data.data || [];
        setProperties(props);
        // Admin con solo 1 propiedad → auto-seleccionarla
        if (props.length === 1 && !isSuperAdmin) {
          setFilterProp(String(props[0].id));
          setSelectedPropObj(props[0]);
        }
      });
    }
  }, [isAdminMultiProp, isSuperAdmin]);

  // F-24: aviso de cruces de horario que devolvió el servidor al reprogramar.
  const location = useLocation();
  useEffect(() => {
    const avisos = location.state?.avisoCruce;
    if (avisos?.length) {
      setAlert({ type: 'warning', message: `Asamblea reprogramada. Estos representantes quedaron con otra asamblea que se cruza con el nuevo horario: ${avisos.join(', ')}. Revisa sus poderes (en Asistentes → Ver poder).` });
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  const showAlert = useCallback((type, message) => {
    setAlert({ type, message });
    cerrarAlertaEn(3500);
  }, [cerrarAlertaEn]);

  // Solo la ÚLTIMA consulta puede tocar la pantalla: si la primera (sin propiedad, antes de que
  // carguen las propiedades) responde tarde, no pisa el resultado de la segunda.
  const ultimaConsulta = useRef(0);
  const fetchAssemblies = useCallback(async () => {
    const miConsulta = ++ultimaConsulta.current;
    const vigente = () => miConsulta === ultimaConsulta.current;
    try {
      setLoading(true);
      setAvisoTodas(null);
      const params = { search, page, limit: PAGE_SIZE };
      // Filtro por propiedad (lo aplica el servidor, también al superadmin)
      if (filterProp) params.property_id = filterProp;
      const { data } = await assemblyService.getAll(params);
      if (!vigente()) return;
      // Si al borrar quedó vacía la última página, retroceder una
      if (!(data.data || []).length && page > 1) { setPage(page - 1); return; }
      setAssemblies(data.data || []);
      setTotal(data.total || 0);
    } catch (err) {
      if (!vigente()) return;
      // «Todas las propiedades» falla si la propiedad de su sesión tiene la membresía vencida
      // (aunque tenga otras al día): se explica y se indica la salida, sin el error genérico.
      if (!filterProp && err.response?.data?.code === 'SUBSCRIPTION_INACTIVE') {
        setAssemblies([]); setTotal(0); // no dejar la lista del conjunto anterior bajo el aviso
        setAvisoTodas('Una de tus propiedades tiene la membresía vencida y por eso no se pueden mostrar todas las asambleas juntas. Elige una propiedad del selector, o cierra sesión y vuelve a entrar.');
      } else {
        showAlert('danger', err.response?.data?.message || 'Error al cargar asambleas');
      }
    } finally {
      if (vigente()) setLoading(false);
    }
  }, [search, page, filterProp, showAlert]);

  useEffect(() => { fetchAssemblies(); }, [fetchAssemblies]);

  const handleDelete = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    setDelError('');
    try {
      await assemblyService.delete(confirmDel.id);
      showAlert('success', 'Asamblea eliminada');
      setConfirmDel(null);
      fetchAssemblies();
    }
    catch (err) { setDelError(err.response?.data?.message || 'No se pudo eliminar. Inténtalo de nuevo.'); }
    finally { setDeleting(false); }
  };

  const aplicarEstado = async (id) => {
    const destino = SIGUIENTE_ESTADO[assemblies.find(a => a.id === id)?.status];
    if (!destino) return showAlert('danger', 'No se pudo saber el estado actual de la asamblea. Recarga la lista e inténtalo de nuevo.');
    try { await assemblyService.updateStatus(id, destino); showAlert('success', 'Estado actualizado'); fetchAssemblies(); }
    catch (err) { showAlert('danger', err.response?.data?.message || 'Error al cambiar estado'); }
  };
  // FINALIZAR y REINICIAR siempre piden confirmar (R219): un clic sin querer no cambia el estado. Iniciar no pide nada (no pierde nada y se puede revertir).
  // Al finalizar con votaciones abiertas, el aviso además las lista.
  const [finalizar, setFinalizar]       = useState(null);   // { id, abiertas, asamblea } (abiertas puede ser [])
  const [finalizando, setFinalizando]   = useState(false);
  const [reiniciar, setReiniciar]       = useState(null);   // { id, asamblea }
  const [reiniciando, setReiniciando]   = useState(false);
  const consultando = useRef(false);   // un segundo clic mientras se consultan las votaciones abiertas no abre otro aviso
  const handleStatusChange = async (id) => {
    const asamblea = assemblies.find(a => a.id === id);
    if (asamblea?.status === 'en_progreso') {
      if (consultando.current) return;
      consultando.current = true;
      try { const abiertas = await votacionesAbiertas(id); setFinalizar({ id, abiertas, asamblea }); }
      finally { consultando.current = false; }
      return;
    }
    if (asamblea?.status === 'finalizada') return setReiniciar({ id, asamblea });
    return aplicarEstado(id);
  };

  const handleOrdenSaved = (id, orden_del_dia) => {
    setAssemblies(prev => prev.map(a => a.id === id ? { ...a, orden_del_dia } : a));
    showAlert('success', 'Orden del día actualizado');
  };

  const handleFilterProp = (propId, elegida) => {
    const propObj = elegida || properties.find(p => String(p.id) === String(propId)) || null;
    setFilterProp(propId);
    setSelectedPropObj(propObj);
    setPage(1);
  };

  // El filtro por propiedad y la paginación los aplica el servidor
  const visibleAssemblies = assemblies;

  // Cambiar de página sube al inicio: el pie queda al final de una lista larga.
  const irAPagina = (n) => { setPage(n); window.scrollTo({ top: 0 }); };

  // Super admin, o admin con varias propiedades sin filtro: una sola lista con columna «Conjunto»
  const variasPropiedades = isSuperAdmin || (isAdminMultiProp && properties.length > 1 && !filterProp);
  // La membresía es por conjunto: se evalúa en cada fila (sin propiedad: el estado global)
  const estadoDe = (a) => {
    if (!a.property) return { readOnly: isSuperAdmin ? isReadOnly : false, blocked: false };
    const sub = getSubscriptionStateForProperty(a.property);
    return { readOnly: sub === 'grace', blocked: sub === 'blocked' };
  };

  return {
    properties, filterProp, selectedPropObj, loading, search, setSearch, page, setPage,
    total, alert, avisoTodas, confirmDel, setConfirmDel, deleting, delError, setDelError, finalizar,
    setFinalizar, finalizando, setFinalizando, reiniciar, setReiniciar, reiniciando, setReiniciando, isSuperAdmin, isAdminMultiProp, isReadOnly, isBlocked,
    abrirReunionStaff, handleDelete, aplicarEstado, handleStatusChange, handleOrdenSaved,
    handleFilterProp, visibleAssemblies, irAPagina, variasPropiedades, estadoDe,
  };
}
